/**
 * WebRTCStateBadge
 *
 * Inline widget on the WebRTCPage that shows the live state of the
 * underlying RTCPeerConnection. It exists so an operator can diagnose
 * a "no video" symptom without opening DevTools:
 *
 *   - ICE stuck in "checking" or "failed" → TURN/network problem
 *   - connectionState = "connected" but tracksReceived = 0 → agent
 *     never sent a video track (binary mismatch or capture pipeline
 *     dead on the agent side)
 *   - tracksReceived > 0 but lastTrackLabel is empty/odd → agent is
 *     sending tracks but they don't match the "screen-N" convention
 *     the multi-pane routing expects (typically a stale binary)
 *
 * The component is intentionally compact: it lives in the page header
 * so it's always visible, even when the DebugPanel is closed. It's
 * hidden entirely when there's no active session (state == null) so
 * it doesn't add noise to the connection flow.
 *
 * Chip colors are wired so a quick glance is enough:
 *   - success (green)  = healthy state
 *   - warning (yellow) = transitional / in-progress
 *   - error   (red)    = broken state, the operator should look
 *   - default (grey)   = not yet attempted or unknown
 */

import { useMemo } from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

export interface WebRTCStateBadgeProps {
  pcConnectionState: RTCPeerConnectionState | null;
  pcIceConnectionState: RTCIceConnectionState | null;
  pcSignalingState: RTCSignalingState | null;
  pcIceGatheringState: RTCIceGatheringState | null;
  tracksReceived: number;
  lastTrackLabel: string | null;
  lastTrackKind: string | null;
}

type ChipColor = 'success' | 'warning' | 'error' | 'default';

const colorFor = (
  state: string | null,
  healthy: readonly string[],
  transitional: readonly string[]
): ChipColor => {
  if (state == null) return 'default';
  if (healthy.includes(state)) return 'success';
  if (transitional.includes(state)) return 'warning';
  return 'error';
};

const iconFor = (color: ChipColor) => {
  switch (color) {
    case 'success':
      return <CheckCircleIcon fontSize="small" />;
    case 'warning':
      return <WarningAmberIcon fontSize="small" />;
    case 'error':
      return <ErrorIcon fontSize="small" />;
    default:
      return <RadioButtonUncheckedIcon fontSize="small" />;
  }
};

export const WebRTCStateBadge: React.FC<WebRTCStateBadgeProps> = ({
  pcConnectionState,
  pcIceConnectionState,
  pcSignalingState,
  pcIceGatheringState,
  tracksReceived,
  lastTrackLabel,
  lastTrackKind,
}) => {
  // ICE healthy: connected/completed. Transitional: checking/new.
  // Anything else (failed, disconnected, closed) is red.
  const iceColor = useMemo(
    () => colorFor(pcIceConnectionState, ['connected', 'completed'], ['checking', 'new']),
    [pcIceConnectionState]
  );
  // PC healthy: connected. Transitional: connecting/new. Anything
  // else (failed, disconnected, closed) is red.
  const pcColor = useMemo(
    () => colorFor(pcConnectionState, ['connected'], ['connecting', 'new']),
    [pcConnectionState]
  );
  // Signaling healthy: stable. Transitional: have-local-offer /
  // have-remote-offer. Closed is red.
  const sigColor = useMemo(
    () => colorFor(pcSignalingState, ['stable'], ['have-local-offer', 'have-remote-offer']),
    [pcSignalingState]
  );
  // Gathering healthy: complete. Transitional: gathering. New is default.
  const gatherColor = useMemo(
    () => colorFor(pcIceGatheringState, ['complete'], ['gathering']),
    [pcIceGatheringState]
  );

  // Tracks: green if we got at least one with the canonical "screen-N"
  // label, yellow if we got tracks but the label looks unexpected, red
  // if the PC is connected but no tracks arrived. Hidden (default) if
  // we haven't tried yet.
  const trackColor: ChipColor = useMemo(() => {
    if (pcConnectionState == null) return 'default';
    if (tracksReceived === 0) {
      return pcConnectionState === 'connected' ? 'error' : 'warning';
    }
    if (lastTrackLabel && /^screen-\d+$/.test(lastTrackLabel)) return 'success';
    return 'warning';
  }, [pcConnectionState, tracksReceived, lastTrackLabel]);

  // If the PC was never created we render nothing. The operator can
  // still see the connection status from the existing `statusChip`
  // on the page header; this widget only adds value once there's
  // something to report.
  if (
    pcConnectionState == null &&
    pcIceConnectionState == null &&
    pcSignalingState == null &&
    pcIceGatheringState == null
  ) {
    return null;
  }

  const trackLabel = lastTrackLabel
    ? `${lastTrackLabel}${lastTrackKind ? ` · ${lastTrackKind}` : ''}`
    : tracksReceived > 0
      ? '(sin label)'
      : '—';

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      data-testid="webrtc-state-badge"
      sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
    >
      <Tooltip title={`RTCPeerConnection.connectionState — ${pcConnectionState ?? 'n/a'}`}>
        <Chip
          size="small"
          icon={iconFor(pcColor)}
          label={`PC ${pcConnectionState ?? '—'}`}
          color={pcColor}
          variant="outlined"
        />
      </Tooltip>
      <Tooltip title={`RTCPeerConnection.iceConnectionState — ${pcIceConnectionState ?? 'n/a'}`}>
        <Chip
          size="small"
          icon={iconFor(iceColor)}
          label={`ICE ${pcIceConnectionState ?? '—'}`}
          color={iceColor}
          variant="outlined"
        />
      </Tooltip>
      <Tooltip title={`RTCPeerConnection.signalingState — ${pcSignalingState ?? 'n/a'}`}>
        <Chip
          size="small"
          icon={iconFor(sigColor)}
          label={`Sig ${pcSignalingState ?? '—'}`}
          color={sigColor}
          variant="outlined"
        />
      </Tooltip>
      <Tooltip title={`RTCPeerConnection.iceGatheringState — ${pcIceGatheringState ?? 'n/a'}`}>
        <Chip
          size="small"
          icon={iconFor(gatherColor)}
          label={`Gather ${pcIceGatheringState ?? '—'}`}
          color={gatherColor}
          variant="outlined"
        />
      </Tooltip>
      <Tooltip
        title={
          tracksReceived === 0
            ? 'Ningún track recibido todavía. Si la PC está "connected" y esto sigue en 0, el agente no está mandando video (binario viejo o pipeline de captura muerto).'
            : `${tracksReceived} track(s) recibido(s). Etiqueta del último: "${trackLabel}". El routing espera "screen-N" (N = índice del monitor).`
        }
      >
        <Chip
          size="small"
          icon={iconFor(trackColor)}
          label={`tracks ${tracksReceived}${lastTrackLabel ? ` · ${lastTrackLabel}` : ''}`}
          color={trackColor}
          variant="outlined"
          data-testid="webrtc-state-badge-tracks"
        />
      </Tooltip>
    </Stack>
  );
};
